import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
} from "@heroui/react";
import { FiMenu } from "react-icons/fi";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { CohortService } from "@/services/cohortService";

const AdminHeader = () => {
  const location = useLocation();
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const [cohortName, setCohortName] = useState<string>("");

  const isCohortDashboardRoute = location.pathname.startsWith("/cohort-dashboard/");
  const isAddNewCohortRoute = location.pathname === "/add-new-cohort";
  const isEditCohortRoute = location.pathname.startsWith("/edit-cohort/");
  const isAssessmentTemplatesPage =
    location.pathname === "/assessment-templates" ||
    location.pathname.startsWith("/assessment-templates/");
  const showBreadcrumbs = !isAssessmentTemplatesPage;

  useEffect(() => {
    if ((isCohortDashboardRoute || isEditCohortRoute) && cohortId) {
      const fetchCohortName = async () => {
        try {
          const cohort = await CohortService.getCohortById(cohortId);
          if (cohort?.name) {
            setCohortName(cohort.name);
          }
        } catch (error) {
          console.error("Error fetching cohort name:", error);
        }
      };
      fetchCohortName();
    } else {
      // The header stays mounted across navigations — clear the name so a
      // previously viewed cohort never leaks into another route's breadcrumb.
      setCohortName("");
    }
  }, [cohortId, isCohortDashboardRoute, isEditCohortRoute]);

  // Render the header on cohort dashboard routes, add-new-cohort, and edit-cohort
  const showHeader = (isCohortDashboardRoute && showBreadcrumbs) || isAddNewCohortRoute || isEditCohortRoute;
  if (!showHeader) {
    return null;
  }

  // Determine breadcrumb text based on route
  const getBreadcrumbLabel = () => {
    if (isAddNewCohortRoute) return "Add New Cohort";
    if (isEditCohortRoute)
      return cohortName ? `Edit Cohort: ${cohortName}` : "Edit Cohort";
    return cohortName || "";
  };

  return (
    <div className="w-full p-2 flex items-center justify-between bg-white sticky top-0 z-30 shadow-sm">
      <div className="flex gap-3">
        <div>
          <FiMenu size={20} className="lg:hidden cursor-pointer" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            variant="light"
            radius="sm"
            size="sm"
            onPress={() => navigate("/active-cohorts")}
            className="border border-gray-300 min-w-8 w-8 h-8"
          >
            <ArrowLeftIcon className="w-4 h-4 text-gray-600" />
          </Button>
          <Breadcrumbs underline="always">
            <BreadcrumbItem
              classNames={{ item: "text-sky-500 text-base cursor-pointer" }}
              onPress={() => navigate("/active-cohorts")}
            >
              Active Cohorts
            </BreadcrumbItem>
            {getBreadcrumbLabel() && (
              <BreadcrumbItem classNames={{ item: "text-gray-700 text-base" }}>
                {getBreadcrumbLabel()}
              </BreadcrumbItem>
            )}
          </Breadcrumbs>
        </div>
      </div>
    </div>
  );
};

export default AdminHeader;
